import React, { useEffect, useState, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Download, Loader2, Trash2, Images, ExternalLink,
  AlertTriangle, Archive, ArrowUpDown, Search, Upload,
} from "lucide-react";
import { Header } from "../components/Header";
import { api, formatApiErrorDetail, resolveAsset } from "../lib/api";

const PAGE_SIZE = 60;

function isVideo(ct) {
  return (ct || "").startsWith("video/");
}

function isImage(ct) {
  return (ct || "").startsWith("image/");
}

function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function AdminMedia() {
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState("newest");
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [zipBusy, setZipBusy] = useState(false);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      window.alert("File is too large — please keep under 8 MB.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    setError("");
    try {
      await api.post("/uploads", formData);
      setQ("");
      await refresh(0, "newest", "");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const refresh = async (newOffset = offset, newSort = sort, newQ = q) => {
    setError("");
    try {
      const queryParam = newQ ? `&q=${encodeURIComponent(newQ)}` : "";
      const r = await api.get(
        `/admin/media?limit=${PAGE_SIZE}&offset=${newOffset}&sort=${encodeURIComponent(newSort)}${queryParam}`,
      );
      setData(r.data);
      setOffset(newOffset);
      setSort(newSort);
      setQ(newQ);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleSearch = (newQ) => {
    refresh(0, sort, newQ);
  };

  useEffect(() => { refresh(0, "newest"); }, []);

  const remove = async (item) => {
    const warn = item.usage
      ? `"${item.original_filename || "this file"}" is currently used by:\n${item.usage}\n\nDelete anyway? It will 404 wherever it's used.`
      : `Delete "${item.original_filename || "this file"}"? It will be removed from the library (bytes stay backed up for safety).`;
    if (!window.confirm(warn)) return;
    setError("");
    try {
      await api.delete(`/admin/media/${item.id}`);
      await refresh(offset);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadAll = async () => {
    if (zipBusy) return;
    setZipBusy(true);
    setError("");
    try {
      const r = await api.get("/admin/media/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `last-media-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setZipBusy(false);
    }
  };

  const total = data?.total || 0;
  const items = data?.items || null;
  const hasNext = items && offset + items.length < total;
  const hasPrev = offset > 0;

  return (
    <div className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]" data-testid="page-admin-media">
      <Header />
      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to admin
        </RouterLink>

        <div className="mt-6 mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-brand-accent">
              <Images size={16} strokeWidth={2.5} />
              <span className="label-kicker">Media library</span>
            </div>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
              All your uploads.
            </h1>
            <p className="mt-2 max-w-xl text-sm text-[var(--brand-body)]">
              Every image and video you&apos;ve ever uploaded — newest first.
              Right-click any thumbnail and choose &quot;Save image as…&quot;
              to download, or grab the whole library as a ZIP.
              {total ? ` ${total} file${total === 1 ? "" : "s"} in total.` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={onUploadFile}
              data-testid="media-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="media-upload-button"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-black disabled:opacity-40"
            >
              {uploading ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Upload size={12} />
              )}
              {uploading ? "Uploading..." : "Upload File"}
            </button>

            <button
              type="button"
              onClick={downloadAll}
              disabled={zipBusy || total === 0}
              data-testid="media-download-all"
              className="inline-flex items-center gap-1.5 rounded-md border border-black/10 bg-white px-4 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[var(--brand-ink)] hover:bg-black/5 disabled:opacity-35"
            >
              {zipBusy ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Archive size={12} />
              )}
              {zipBusy ? "Building ZIP…" : "Download all (.zip)"}
            </button>
          </div>
        </div>

        {/* Sort & Search toolbar — refetches the current page in the new order/search query */}
        <div className="mb-6 flex flex-wrap items-center gap-4 rounded-md border border-black/10 bg-[#F8FAFC] p-3">
          <label className="inline-flex items-center gap-2">
            <ArrowUpDown size={14} className="text-[var(--brand-body)]" />
            <span className="label-kicker text-[var(--brand-body)]">Sort by</span>
            <select
              value={sort}
              onChange={(e) => refresh(0, e.target.value, q)}
              data-testid="media-sort"
              className="rounded-md border border-black/15 bg-white px-2 py-1.5 text-sm outline-none focus:border-[#0A0F17]"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="name_asc">Name (A → Z)</option>
              <option value="name_desc">Name (Z → A)</option>
              <option value="size_desc">Size (Largest first)</option>
              <option value="size_asc">Size (Smallest first)</option>
              <option value="used_first">Used first</option>
              <option value="unused_first">Unused first</option>
            </select>
          </label>

          <div className="h-5 w-px bg-black/10 hidden sm:block" aria-hidden />

          <label className="flex-1 min-w-[200px] inline-flex items-center gap-2">
            <Search size={14} className="text-[var(--brand-body)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search filenames..."
              data-testid="media-search"
              className="w-full rounded-md border border-black/15 bg-white px-3 py-1.5 text-sm outline-none focus:border-[#0A0F17]"
            />
          </label>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="media-error"
          >
            {error}
          </p>
        ) : null}

        {items === null ? (
          <p className="text-sm text-[var(--brand-body)]">Loading…</p>
        ) : items.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-black/15 p-10 text-center"
            data-testid="media-empty"
          >
            <p className="font-display text-lg font-bold">No uploads yet.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Add an image to a blog post or theme setting and it&apos;ll appear here.
            </p>
          </div>
        ) : (
          <ul
            className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            data-testid="media-list"
          >
            {items.map((m) => (
              <MediaCard key={m.id} item={m} onDelete={() => remove(m)} />
            ))}
          </ul>
        )}

        {items && items.length > 0 ? (
          <div className="mt-8 flex items-center justify-between" data-testid="media-pager">
            <p className="text-xs text-[var(--brand-body)]">
              Showing {offset + 1}–{offset + items.length} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => refresh(Math.max(0, offset - PAGE_SIZE), sort)}
                disabled={!hasPrev}
                data-testid="media-pager-prev"
                className="inline-flex items-center gap-1 rounded-md border border-black/15 px-3 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
              <button
                type="button"
                onClick={() => refresh(offset + PAGE_SIZE, sort)}
                disabled={!hasNext}
                data-testid="media-pager-next"
                className="inline-flex items-center gap-1 rounded-md border border-black/15 px-3 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}

function MediaCard({ item, onDelete }) {
  const url = resolveAsset(item.url);
  const img = isImage(item.content_type);
  const vid = isVideo(item.content_type);
  const [usage, setUsage] = useState(item.usage || null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  useEffect(() => {
    // If usage is already provided (e.g. from usage-based sorts where backend computed it), use it!
    if (item.usage !== undefined && item.usage !== null) {
      setUsage(item.usage);
      return;
    }
    let alive = true;
    setLoadingUsage(true);
    api.get(`/admin/media/${item.id}/usage`)
      .then((r) => { if (alive) setUsage(r.data?.usage || null); })
      .catch(() => { if (alive) setUsage(null); })
      .finally(() => { if (alive) setLoadingUsage(false); });
    return () => { alive = false; };
  }, [item.id, item.usage]);

  return (
    <li
      className="group flex flex-col overflow-hidden rounded-md border border-black/10 bg-white"
      data-testid={`media-card-${item.id}`}
    >
      <div className="relative aspect-square w-full bg-[#F8FAFC]">
        {img ? (
          <img
            src={url}
            alt={item.original_filename || ""}
            loading="lazy"
            className="absolute inset-0 m-auto h-full w-full object-cover"
          />
        ) : vid ? (
          <video
            src={url}
            className="absolute inset-0 m-auto h-full w-full object-cover"
            preload="metadata"
            muted
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[var(--brand-body)]">
            <Images size={28} />
          </div>
        )}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          download={item.original_filename || true}
          data-testid={`media-${item.id}-open`}
          aria-label="Open in new tab"
          title="Open in new tab to download"
          className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-[var(--brand-ink)] backdrop-blur opacity-0 transition-opacity hover:bg-white group-hover:opacity-100"
        >
          <ExternalLink size={12} />
        </a>
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <p
          className="truncate font-display text-sm font-bold"
          title={item.original_filename || ""}
        >
          {item.original_filename || "(no filename)"}
        </p>
        <p className="text-xs text-[var(--brand-body)]">
          {formatBytes(item.size)} · {formatDate(item.created_at)}
        </p>
        <p
          className={`mt-1 truncate text-xs ${
            usage ? "text-[var(--brand-body)]" : "text-[#E63946]"
          }`}
          title={usage || ""}
          data-testid={`media-${item.id}-usage`}
        >
          {loadingUsage ? (
            <span className="text-slate-400 italic">scanning...</span>
          ) : usage ? (
            <>Used in: <span className="font-semibold">{usage}</span></>
          ) : (
            <span className="inline-flex items-center gap-1">
              <AlertTriangle size={11} /> Unused
            </span>
          )}
        </p>
        <div className="mt-2 flex items-center gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            download={item.original_filename || true}
            data-testid={`media-${item.id}-download`}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-black/15 px-2 py-1.5 font-display text-xs font-bold hover:border-[#0A0F17]"
          >
            <Download size={11} /> Download
          </a>
          <button
            type="button"
            onClick={onDelete}
            data-testid={`media-${item.id}-delete`}
            className="inline-flex items-center justify-center rounded-md border border-[#E63946]/40 px-2 py-1.5 text-[#E63946] hover:bg-[#E63946] hover:text-white"
            aria-label="Delete this file"
            title="Delete this file"
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    </li>
  );
}
